import { useState } from "react"
import {
  Dialog,
  Checkbox,
  DialogBody,
  DialogFooter,
  Icon,
  Spinner,
  Tag,
} from "@blueprintjs/core"
import { Popover2, Popover2InteractionKind } from "@blueprintjs/popover2"
import React from "react"
import CustomButton from "components/Atoms/CustomButton"
import CustomTag from "components/Atoms/CustomTag"
import { Files, PlusCircle } from "@phosphor-icons/react"
import {
  ShipmentPlanType,
  useMatchProfilesByCustIdQuery,
} from "generated/graphql"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import ButtonMutation from "components/Molecules/ButtonMutation"
import { ADD_BULK_APPROVE_TRADE_PROFILE } from "graphql/production/salesOrder.mutation.gql"
import { ErrorToast } from "utils/AppToaster"
import QueryBar from "components/Common/QueryBar"
import TablePagination from "components/Molecules/TablePagination"
import dayjs from "dayjs"

export type SalesOrderProps = {
  salesOrder: ShipmentPlanType
  refetch: () => void
}

const getLightBgColor = (color?: string | null) => {
  if (!color) return undefined
  return `color-mix(in srgb, ${color} 8%, white)`
}

const getDarkBorderColor = (color?: string | null) => {
  if (!color) return undefined
  return `color-mix(in srgb, ${color} 70%, black)`
}

const getTagConfig = (tags?: (string | null)[] | null) => {
  if (tags?.includes("fail")) return { intent: "danger", text: "FAIL" }
  if (tags?.includes("alert")) return { intent: "warning", text: "ALERT" }
  if (tags?.includes("trace")) return { intent: "success", text: "Trace" }
  return {
    intent: "primary",
    text: tags?.join(", ").toUpperCase() || "-",
  }
}

const ChemicalListing = ({ observations }: { observations: any[] }) => (
  <div className="rounded-lg overflow-hidden border border-gray-300 min-w-[320px] max-h-[300px] overflow-y-auto">
    <div className="bg-blue-100 px-4 py-2">
      <h6 className="text-blue-600 font-semibold text-sm">Chemical Listing</h6>
    </div>
    <table className="w-full text-sm bg-white">
      <thead>
        <tr className="text-xs">
          <th className="text-left py-2 px-4 text-gray-600">
            Quality Parameter
          </th>
          <th className="text-left py-2 px-4 text-gray-600">
            Trace/Alert/Fail (tags)
          </th>
          <th className="text-right py-2 px-4 text-gray-600">
            Detection Count
          </th>
        </tr>
      </thead>
      <tbody>
        {observations.length === 0 ? (
          <tr>
            <td
              colSpan={3}
              className="text-center py-4 text-gray-500 font-medium"
            >
              No Observations Available
            </td>
          </tr>
        ) : (
          observations.map((obs, idx) => {
            const tag = getTagConfig(obs?.tags)
            return (
              <tr key={obs?.id ?? idx} className="border-t border-gray-300">
                <td className="px-4 py-2 font-medium text-gray-800">
                  {obs?.qualityParameter?.name ?? "-"}
                </td>
                <td className="px-4 py-2 text-left">
                  <Tag minimal round intent={tag.intent as any}>
                    {tag.text}
                  </Tag>
                </td>
                <td className="px-4 py-2 text-right">
                  {obs?.detectionCount ?? "-"}
                </td>
              </tr>
            )
          })
        )}
      </tbody>
    </table>
  </div>
)

const SOModal = ({ salesOrder, refetch }: SalesOrderProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [tempSelected, setTempSelected] = useState<string[]>([])
  const [search, setSearch] = useState("")
  const [page, setPage] = useState(0)

  const toggleSelect = (id: string) => {
    setTempSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const handleOpen = () => {
    const approvedProfiles = salesOrder?.approvedtradeprofileSet ?? []
    const selectedIds = approvedProfiles.map(ap => ap?.tradeProfile?.id ?? "")

    if (salesOrder?.soNo === "" || salesOrder?.soNo == "nan") {
      ErrorToast("Sales Order no. is required")
      return
    }

    setTempSelected(selectedIds)
    setIsOpen(true)
  }

  const { data: sData, loading } = useMatchProfilesByCustIdQuery({
    variables: {
      custId: salesOrder?.exportCustomer?.id ?? "",
      shipmentPlanId: salesOrder?.id ?? "",
      search: search,
      limit: 10,
      offset: page * 10,
    },
    fetchPolicy: "network-only",
    skip: !isOpen,
  })

  const metadata = salesOrder?.metadata ? JSON.parse(salesOrder.metadata) : null
  const isEuro = metadata?.is_euro ?? false
  const euroBoxes = metadata?.euro_pallet_box_count ?? 0
  const regularBoxes = metadata?.regular_pallet_box_count ?? 0
  const skuFilled = salesOrder?.skuFilled ?? 0

  let requiredBoxes = 0

  if (skuFilled > 0) {
    if (isEuro) {
      requiredBoxes = euroBoxes + (skuFilled - 1) * regularBoxes
    } else {
      requiredBoxes = skuFilled * regularBoxes
    }
  }

  let varietyBoxAllocation: Record<string, number> = {}
  if (salesOrder?.varietyType === "MIX") {
    const varieties = salesOrder?.varieties ?? []
    const varietyCount = varieties.length
    const baseCount = requiredBoxes / varietyCount

    varieties.forEach(v => {
      if (v?.id) {
        varietyBoxAllocation[v.id] = baseCount
      }
    })
  }

  return (
    <>
      <div onClick={handleOpen} className="cursor-pointer text-center ">
        {salesOrder?.approvedtradeprofileSet?.length === 0 ? (
          <div className="flex items-center justify-center gap-2">
            <PlusCircle size={20} color="green" /> Assign MH Code{" "}
          </div>
        ) : (
          <OverFlowMenu
            intent="primary"
            icon="briefcase"
            className="w-40 flex items-center justify-center"
            items={
              salesOrder.approvedtradeprofileSet?.map(atp => ({
                id: atp?.id ?? "",
                name: atp?.tradeProfile?.certificateNumber ?? "--",
              })) ?? []
            }
          />
        )}
      </div>

      <Dialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <Files size={24} /> Select MH Code
          </div>
        }
        canEscapeKeyClose
        canOutsideClickClose
        style={{ height: "80vh" }}
        className="bg-white max-w-3xl w-full"
      >
        <DialogBody className="max-h-[70vh] overflow-y-auto">
          <div className="space-y-3">
            {/* Sales Order Info */}
            <div className="grid grid-cols-2 gap-y-3 gap-x-8 text-xs mt-6">
              <div className="space-y-3">
                <p>
                  <b>Sales Order no. (SO):</b> <br />
                  {salesOrder?.soNo ?? "--"}
                </p>
                <p>
                  <b>Color Group:</b> <br />
                  {salesOrder?.colourGroup?.name ?? "--"}
                </p>
                <p>
                  <b>Variety:</b> <br />
                  {salesOrder?.varieties?.map(v => v?.name).join(", ") ?? "--"}
                </p>
                <p>
                  <b>Primary Packing:</b> <br />
                  {salesOrder?.primaryPackMaterial?.name ?? "--"}
                </p>
                <p>
                  <b>Label Type:</b> <br />
                  {salesOrder?.labels ?? "--"}
                </p>
              </div>
              <div className="space-y-3">
                <p>
                  <b>Customer Name:</b> <br />
                  {salesOrder?.exportCustomer?.name ?? "--"}
                </p>
                <p>
                  <b>Packing Type:</b> <br />
                  <CustomTag
                    intent={
                      salesOrder?.varietyType === "MIX" ? "primary" : "warning"
                    }
                    round
                    minimal
                  >
                    {salesOrder?.varietyType ?? "--"}
                  </CustomTag>
                </p>
                <p>
                  <b>Packing Format (SKU):</b> <br />
                  {salesOrder?.skuType?.name ?? "--"}
                </p>
                <p>
                  <b>Secondary Packing Material:</b> <br />
                  {salesOrder?.secondaryPackMaterial?.name ?? "--"}
                </p>
              </div>
            </div>

            {/* Summary Info */}
            <div className="bg-blue-50 text-sm p-3 rounded border border-slate-200">
              <div>
                <b>Packing Format:</b> {salesOrder?.skuType?.name ?? "--"} |{" "}
                <b>Required Boxes:</b> {requiredBoxes.toLocaleString()}
              </div>
              <div className="text-gray-600 text-xs">
                {isEuro ? (
                  <>
                    Includes <b>1</b> Euro pallet with <b>{euroBoxes}</b> boxes
                    and <b>{skuFilled - 1}</b> regular pallet
                    {skuFilled - 1 > 1 ? "s" : ""} with <b>{regularBoxes}</b>{" "}
                    boxes each.
                  </>
                ) : (
                  <>
                    Includes <b>{skuFilled}</b> regular pallet
                    {skuFilled > 1 ? "s" : ""}, each with <b>{regularBoxes}</b>{" "}
                    boxes.
                  </>
                )}
              </div>
              <hr className="border-t-1 border-dashed border-slate-300 my-4" />

              <div className="bg-blue-50 rounded border border-slate-200 flex items-center justify-between">
                <div className="text-xs">
                  Make sure selected MH Codes available boxes should fulfilled
                  the required boxes.{" "}
                </div>
              </div>
            </div>

            <div className="flex gap-4 items-center mb-2">
              <QueryBar
                value={search}
                onChange={val => {
                  setSearch(val)
                  setPage(0)
                }}
                placeholder="Search by MH Code"
              />
              <TablePagination
                disabledNext={sData?.matchProfilesByCustId?.length !== 10}
                className="my-0"
                page={page}
                onPageChange={p => setPage(p)}
              />
            </div>

            {/* MH Code List */}
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <Spinner size={30} intent="primary" />
              </div>
            ) : sData?.matchProfilesByCustId?.length ? (
              sData.matchProfilesByCustId.map(gp => (
                <div
                  key={gp?.id}
                  style={{
                    border: "1px solid",
                    borderColor: getDarkBorderColor(gp?.color),
                    backgroundColor: getLightBgColor(gp?.color),
                  }}
                  className={`flex flex-col gap-2 p-5 rounded-lg border ${
                    !gp?.color ? "border-slate-200" : ""
                  } ${
                    !gp?.color && tempSelected.includes(gp?.id ?? "")
                      ? "bg-[#EBF0F4]"
                      : ""
                  }`}
                >
                  <div className="flex items-start justify-between w-full">
                    <Checkbox
                      checked={tempSelected.includes(gp?.id ?? "")}
                      onChange={() => gp?.id && toggleSelect(gp.id)}
                    />
                    <div className="space-y-2 w-full">
                      <div className="flex flex-col gap-1">
                        <div className="flex justify-between">
                          <b>{gp?.certificateNumber}</b>
                          <Popover2
                            interactionKind={Popover2InteractionKind.HOVER}
                            placement="left"
                            portalClassName="vt-modal-portal"
                            hoverOpenDelay={100}
                            content={
                              <ChemicalListing
                                observations={
                                  gp?.plotCommodity?.lastSample?.observations ??
                                  []
                                }
                              />
                            }
                          >
                            <Icon
                              icon="info-sign"
                              className="cursor-pointer text-blue-600"
                            />
                          </Popover2>
                        </div>
                        {gp?.reason && (
                          <div className="text-xs text-gray-700 italic">
                            {gp.reason}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 justify-between items-center">
                        <div>
                          <div>
                            {gp?.plotCommodity?.plot?.farmer?.fullName ?? "--"}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {gp?.plotCommodity?.plot?.address?.village ?? "--"}
                          </div>
                        </div>

                        <div className="flex flex-col gap-1 items-end">
                          <CustomTag minimal intent="success">
                            Available Qty <b>{gp.availableYield} kg</b> /{" "}
                            {Math.floor(
                              gp.availableYield /
                                (salesOrder?.skuType?.capacity ?? 1)
                            )}{" "}
                            boxes
                          </CustomTag>
                          {(gp?.extraAvailableYield ?? 0) > 0 && (
                            <CustomTag minimal intent="primary">
                              Extra Available:{" "}
                              <b>{gp.extraAvailableYield} kg</b> /{" "}
                              {Math.floor(
                                (gp?.extraAvailableYield ?? 0) /
                                  (salesOrder?.skuType?.capacity ?? 1)
                              )}{" "}
                              boxes
                            </CustomTag>
                          )}
                        </div>
                      </div>

                      <div className="flex gap-2 justify-between items-start">
                        <div>
                          <div className="text-gray-700 text-sm">
                            {gp?.plotCommodity?.variety?.name ?? "--"}
                          </div>
                          <div className="text-gray-500 text-xs">Variety</div>
                        </div>
                        <div className="flex gap-3">
                          <div className="flex flex-col gap-1 items-end">
                            <div className="text-xs text-gray-700 font-semibold">
                              Main
                            </div>
                            <div className="text-orange-600 font-bold">
                              {gp.plotYeild.toLocaleString()} kg
                            </div>
                            <div className="text-gray-500 text-xs">
                              {Math.floor(
                                gp.plotYeild /
                                  (salesOrder?.skuType?.capacity ?? 1)
                              )}{" "}
                              boxes
                            </div>
                          </div>

                          {(gp?.extraPlotYeild ?? 0) > 0 && (
                            <>
                              <div className="border-l border-gray-300"></div>
                              <div className="flex flex-col gap-1 items-end">
                                <div className="text-xs text-blue-600 font-semibold">
                                  Extra
                                </div>
                                <div className="text-blue-600 font-bold">
                                  {gp.extraPlotYeild.toLocaleString()} kg
                                </div>
                                <div className="text-gray-500 text-xs">
                                  {Math.floor(
                                    (gp?.extraPlotYeild ?? 0) /
                                      (salesOrder?.skuType?.capacity ?? 1)
                                  )}{" "}
                                  boxes
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <hr className="border-t-1 border-dashed border-slate-300 my-4" />

                      <div className="flex items-center justify-between text-xs">
                        <div>
                          <b>No. of Detections:</b> <br />{" "}
                          {gp?.plotCommodity?.lastSample?.detectionCount}
                        </div>
                        <div>
                          <b>Total Percentage:</b> <br />{" "}
                          {gp?.plotCommodity?.lastSample?.totalDetection}
                        </div>
                        <div>
                          <b>Individual Higher Value:</b> <br />{" "}
                          {gp?.plotCommodity?.lastSample?.maxDetection}
                        </div>
                        <div>
                          <b>Created On:</b> <br />{" "}
                          {gp?.plotCommodity?.lastSample?.created
                            ? dayjs(gp.plotCommodity.lastSample.created).format(
                                "DD MMM 'YY"
                              )
                            : "--"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 text-sm py-6">
                No MH Codes available
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <div className="flex justify-between items-center w-full">
            <CustomButton onClick={() => setIsOpen(false)} round>
              Cancel
            </CustomButton>

            <ButtonMutation
              mutation={ADD_BULK_APPROVE_TRADE_PROFILE}
              variables={{
                input: {
                  emptyShipmentPlanId: salesOrder?.id ?? "",
                  items: tempSelected.map(id => {
                    return {
                      tradeProfileId: id,
                      shipmentPlanId: salesOrder?.id ?? "",
                    }
                  }),
                },
              }}
              intent="success"
              text="Confirm"
              onCompleted={data => {
                if (data?.addBulkApproveTradeProfile?.ok === false) {
                  ErrorToast("It is assign to the palletization")
                }

                setIsOpen(false)
                refetch()
                setTempSelected([])
              }}
            />
          </div>
        </DialogFooter>
      </Dialog>
    </>
  )
}

export default SOModal
